import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { Home, ArrowRight, Search, Link2, QrCode, FileText, ClipboardList } from 'lucide-react';

// =============================================================================
// Animated Search Icon
// =============================================================================

function SearchAnimation() {
  return (
    <div className="relative w-32 h-32 mx-auto mb-8">
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-orange-500/20 rounded-full blur-2xl animate-pulse" />

      {/* Main icon container */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="relative w-full h-full flex items-center justify-center"
      >
        {/* Orbiting asset icons */}
        {[
          { Icon: Link2, delay: 0 },
          { Icon: QrCode, delay: 0.5 },
          { Icon: FileText, delay: 1 },
          { Icon: ClipboardList, delay: 1.5 },
        ].map(({ Icon, delay }, i) => (
          <motion.div
            key={i}
            className="absolute"
            animate={{
              rotate: [0, 360],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'linear',
              delay: delay,
            }}
            style={{
              transformOrigin: 'center center',
            }}
          >
            <motion.div
              className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center"
              style={{
                transform: `translateY(-48px)`,
              }}
              animate={{
                rotate: [0, -360], // Counter-rotate to keep icon upright
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear',
                delay: delay,
              }}
            >
              <Icon className="w-4 h-4 text-muted-foreground" />
            </motion.div>
          </motion.div>
        ))}

        {/* Central search icon */}
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-orange-500 flex items-center justify-center shadow-lg shadow-primary/25"
        >
          <Search className="w-10 h-10 text-white" />
        </motion.div>
      </motion.div>
    </div>
  );
}

// =============================================================================
// 404 Page Content
// =============================================================================

export function NotFoundPage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  return (
    <div className="min-h-[calc(100vh-12rem)] flex items-center justify-center px-4 py-16 relative bg-background">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      <div className="text-center max-w-lg relative z-10">
        <SearchAnimation />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-6xl font-bold text-foreground mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-foreground mb-4">
            We couldn&apos;t find that
          </h2>
          <p className="text-muted-foreground mb-2">
            This link, page, or resource may have been moved, expired, or doesn&apos;t exist.
          </p>
          {code && (
            <p className="text-muted-foreground/80 text-sm mb-6">
              Looking for:{' '}
              <code className="text-primary bg-primary/10 px-2 py-0.5 rounded font-mono">
                arml.ink/{code}
              </code>
            </p>
          )}
        </motion.div>

        {/* Suggestions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card border border-border rounded-xl p-6 mb-8 text-left"
        >
          <h3 className="font-medium text-foreground mb-3">This could happen if:</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              The short code was mistyped or copied incorrectly
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              The link expired or was deactivated by its creator
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              The page or resource was moved to a new location
            </li>
          </ul>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-6 py-3 font-semibold text-zinc-900 transition-all hover:scale-105 shadow-lg shadow-amber-500/25"
          >
            <Home className="w-4 h-4" />
            Go Home
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-muted hover:bg-muted/80 border border-border px-6 py-3 font-semibold text-foreground transition-all"
          >
            Create Something New
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
